import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    Typography, Chip, Toolbar,
} from '@mui/material';
import {
    DashboardRounded, PeopleRounded, AccessTimeRounded, BarChartRounded,
    StarRounded, BlockRounded, SettingsRounded,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

export const SIDEBAR_WIDTH = 260;

// Nav items point at pages built in later phases (see docs/PLANNING.md).
// `path: null` renders as a disabled "Soon" item rather than a dead link,
// so the sidebar is complete from day one without shipping 404s.
// `featureKeys` names which privilege(s) govern visibility — an item shows
// if the caller isn't 'n/a' on at least one of them (Reports covers two
// tabs backed by two different features, so either being accessible is
// enough to show the page). `adminOnly` items (app version management)
// aren't part of the synced privilege model at all — admin-only by design.
const NAV_ITEMS = [
    { label: 'Dashboard', icon: <DashboardRounded />, path: '/', featureKeys: ['dashboard'] },
    { label: 'Employees', icon: <PeopleRounded />, path: '/employees', featureKeys: ['settings.users'] },
    { label: 'Attendance', icon: <AccessTimeRounded />, path: '/attendance', featureKeys: ['reports.attendance'] },
    { label: 'Reports', icon: <BarChartRounded />, path: '/usage', featureKeys: ['reports.activity_summary', 'reports.web_app_usage'] },
    { label: 'Productivity Rating', icon: <StarRounded />, path: '/rating', featureKeys: ['settings.productivity_rating'] },
    { label: 'App Blocking', icon: <BlockRounded />, path: '/blocking', featureKeys: ['settings.productivity_rating'] },
    { label: 'Settings', icon: <SettingsRounded />, path: '/settings', adminOnly: true },
];

function SidebarContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, accessLevel } = useAuth();

    const visibleItems = NAV_ITEMS.filter((item) => {
        if (item.adminOnly) return isAdmin;
        if (isAdmin) return true;
        return item.featureKeys.some((key) => accessLevel(key) !== 'n/a');
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar sx={{ px: 3, gap: 1.5 }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <svg width="18" height="18" viewBox="0 0 60 60" fill="none">
                        <path d="M30 8L50 28V52H10V28L30 8Z" fill="white" />
                    </svg>
                </Box>
                <Typography variant="h3" sx={{ fontSize: '1.1rem' }}>ZycoTM</Typography>
            </Toolbar>

            <List sx={{ px: 2, py: 1, flex: 1 }}>
                {visibleItems.map((item) => {
                    const selected = item.path !== null && (
                        item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
                    );
                    return (
                        <ListItemButton
                            key={item.label}
                            selected={selected}
                            disabled={item.path === null}
                            onClick={() => item.path !== null && navigate(item.path)}
                            sx={{
                                borderRadius: 2.5,
                                mb: 0.5,
                                py: 1.1,
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(79,70,229,0.25)',
                                    '&:hover': { bgcolor: 'rgba(79,70,229,0.32)' },
                                },
                                '&.Mui-disabled': { opacity: 0.5 },
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 38,
                                color: selected ? 'primary.light' : 'text.secondary',
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: selected ? 600 : 500,
                                    color: selected ? 'text.primary' : 'text.secondary',
                                }}
                            />
                            {item.path === null && (
                                <Chip label="Soon" size="small" sx={{
                                    height: 20, fontSize: '0.65rem',
                                    bgcolor: 'rgba(255,255,255,0.08)', color: 'text.secondary',
                                }} />
                            )}
                        </ListItemButton>
                    );
                })}
            </List>

            <Box sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    ZycoTM Dashboard v1.0
                </Typography>
            </Box>
        </Box>
    );
}

// Renders as a permanent sidebar on desktop widths, and a temporary
// (overlay) drawer on mobile, controlled by the parent layout.
export default function Sidebar({ mobileOpen, onMobileClose }) {
    return (
        <Box component="nav" sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onMobileClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
                }}
            >
                <SidebarContent />
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, borderRight: '1px solid rgba(255,255,255,0.08)' },
                }}
                open
            >
                <SidebarContent />
            </Drawer>
        </Box>
    );
}
